import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import { Language, Utils } from "../../../utils";
import { user as userActions } from "../../../actions";
import { Button, InformationTooltip, ErrorIcon } from "../../../components";

import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import makeClasses from "../styles";

import clsx from "clsx";
import { Box, InputLabel } from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";

const StepOne = ({ nextStep, code }) => {
  const dispatch = useDispatch();
  const classes = makeClasses();

  const { loading, languageTexts } = useSelector((state) => ({
    loading: state.user.loading,
    languageTexts: state.language.texts || {},
  }));

  const i18n = Language(languageTexts);

  const [inputRut, setInputRut] = useState("");
  const [hasRutError, setHasRutError] = useState(false);
  const [mccs, setMcc] = useState([]);
  const [inputMcc, setInputMcc] = useState(null);
  const [inputName, setInputName] = useState("");
  const [businessNameTypes, setBusinessNameTypes] = useState([]);

  const [inputBusinessNameTypes, setInputBusinessNameTypes] = useState(null);

  const canMoveToStep2 =
    inputRut.trim().length > 0 &&
    !hasRutError &&
    inputMcc &&
    inputBusinessNameTypes &&
    inputName.trim().length > 0;

  useEffect(() => {
    dispatch(
      userActions.getMccs((mccs) => {
        setMcc(mccs);
      }),
    );
  }, []);

  useEffect(() => {
    dispatch(
      userActions.getBusinessNameTypes((businessNameTypes) => {
        setBusinessNameTypes(businessNameTypes);
      }),
    );
  }, []);

  const handleRutChange = (e) => {
    const value = e.target.value;
    if (value !== "" && !/^\d+$/.test(value)) return;
    setInputRut(value);
    setHasRutError(value.length > 0 && !Utils.inputs.rutInput.regex.test(value.trim()));
  };

  const handleNext = () => {
    if (!Utils.inputs.rutInput.regex.test(inputRut.trim())) {
      setHasRutError(true);
      return;
    }

    dispatch(
      userActions.merchantStartAffiliation(
        {
          countryISO: "UY",
          documentTypeKey: "RUT",
          document: inputRut.trim(),
          name: inputName.trim(),
          affiliationCode: code,
          mccId: inputMcc,
          businessNameTypeId: inputBusinessNameTypes,
        },
        (merchantStartAffiliationResult) => {
          if (merchantStartAffiliationResult?.traceId) {
            nextStep({
              traceId: merchantStartAffiliationResult.traceId,
              inputRut: inputRut,
              inputName: inputName,
              inputMcc: inputMcc,
              inputBusinessNameTypes: inputBusinessNameTypes,
              requiresPosExemption:
                merchantStartAffiliationResult?.requiresPosExemption,
            });
          } else {
            return toast.error(
              i18n.get("LegalEntityRegistration.AffiliationError"),
              { autoClose: 5000 },
            );
          }
        },
      ),
    );
  };

  const _getOptionLabel = (array) => (option) => {
    let foundOption = null;

    if (array === "mccs") {
      foundOption = mccs.find((m) => m.id === option);
    }
    if (array === "businessNameTypes") {
      foundOption = businessNameTypes.find((b) => b.id === option);
    }

    if (foundOption) {
      return foundOption.name;
    }

    return "";
  };

  const _handleOnChangeMcc = (e, newValue) => {
    setInputMcc(newValue);
  };

  const _handleOnChangeBusinessNameTypes = (e, newValue) => {
    setInputBusinessNameTypes(newValue);
  };

  return (
    <React.Fragment>
      <div className={classes.formContent}>
        <div className={classes.formTitleContainer}>
          <div className={classes.formTitle}>
            {i18n.get("LegalEntityRegistration.StepFormTitle")}
          </div>
          <div className={classes.formText}>
            {i18n.get("LegalEntityRegistration.Step1FormText")}
          </div>
        </div>
        <form layout="vertical" onSubmit={handleNext} className={classes.form}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <InputLabel className={classes.formLabel} htmlFor="inputRut">
                {i18n.get("LegalEntityRegistration.InputRutTitle")}
              </InputLabel>
              <TextField
                error={hasRutError}
                placeholder={i18n.get("LegalEntityRegistration.InputRutTitle")}
                className={classes.formInput}
                onChange={handleRutChange}
                classes={{ root: classes.formInput }}
                variant="outlined"
                value={inputRut}
                inputProps={{ inputMode: "numeric" }}
              />
              {hasRutError && (
                <div className={classes.errorMessageContainer}>
                  <ErrorIcon />
                  <div className={classes.errorMessage}>
                    {i18n.get("LegalEntityRegistration.RegisterInvalidRut")}
                  </div>
                </div>
              )}
            </Grid>
            <Grid item xs={12}>
              <InputLabel className={classes.formLabel}>
                {i18n.get("LegalEntityRegistration.InputBusinessNameLabel")}
              </InputLabel>
              <TextField
                placeholder={i18n.get("LegalEntityRegistration.InputName")}
                className={classes.formInput}
                onChange={(e) => setInputName(e.target.value)}
                classes={{ root: classes.formInput }}
                variant="outlined"
                value={inputName}
              />
            </Grid>
            <Grid item xs={12}>
              <InputLabel className={classes.formLabel}>
                {i18n.get("LegalEntityRegistration.InputMccLabel")}
              </InputLabel>
              <Autocomplete
                options={mccs.map((m) => m.id)}
                getOptionLabel={_getOptionLabel("mccs")}
                onChange={_handleOnChangeMcc}
                classes={{ inputRoot: classes.formInputSelect }}
                value={inputMcc}
                renderInput={(params) => (
                  <TextField
                    className={classes.formInput}
                    classes={{ root: classes.formInput }}
                    variant="outlined"
                    placeholder={i18n.get("LegalEntityRegistration.InputMcc")}
                    {...params}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Box display="flex" className={classes.formLabelContainer}>
                <InputLabel>
                  {i18n.get("LegalEntityRegistration.InputLegalNature")}
                </InputLabel>
                <InformationTooltip
                  title={i18n.get("LegalEntityRegistration.LegalNatureTooltip")}
                />
              </Box>
              <Autocomplete
                options={businessNameTypes.map((b) => b.id)}
                getOptionLabel={_getOptionLabel("businessNameTypes")}
                onChange={_handleOnChangeBusinessNameTypes}
                classes={{ inputRoot: classes.formInputSelect }}
                value={inputBusinessNameTypes}
                renderInput={(params) => (
                  <TextField
                    className={classes.formInput}
                    classes={{ root: classes.formInput }}
                    variant="outlined"
                    placeholder={i18n.get(
                      "LegalEntityRegistration.InputLegalNature",
                    )}
                    {...params}
                  />
                )}
              />
            </Grid>
          </Grid>
        </form>
      </div>
      <div className={classes.termsWrapper}>
        <div
          className={clsx(
            classes.formSubmitContainer,
            classes.formSubmitContainerWithOneButton,
            classes.formSubmitContainerWithNoMargin,
          )}>
          <Button
            className={classes.formSubmitButton}
            disabled={loading || !canMoveToStep2}
            onClick={handleNext}>
            {i18n.get("LegalEntityRegistration.NextButton")}
          </Button>
        </div>
      </div>
    </React.Fragment>
  );
};
StepOne.id = "com.Handy.LegalEntityRegistration.StepOne";
export default StepOne;
